import { useState, useMemo } from 'react';
import { TrendingDown, Building2, AlertCircle, Clock } from 'lucide-react';
import { Card } from '@components/ui';
import clsx from 'clsx';
import { CategoryGrid } from '../shared';

/**
 * GiderAnalizi — Gider Analizi Sekmesi
 * Filtreler: Kategori bazlı filtreleme, Ödeme durumu filtresi
 */
export default function GiderAnalizi({ summary, payables, allTransactions, formatCurrency, formatDate }) {
    const totalExpense = summary.expense?.total || 0;

    // Filtre: gider kategorisi gösterim
    const [categoryFilter, setCategoryFilter] = useState('all');
    // Filtre: borç durumu
    const [debtFilter, setDebtFilter] = useState('all');

    // Expense items
    const allExpenseItems = [
        { key: 'materials', label: 'Malzeme', value: summary.expense?.materials || 0, icon: '🧱' },
        { key: 'labor', label: 'İşçilik', value: summary.expense?.labor || 0, icon: '👷' },
        { key: 'infrastructure', label: 'Altyapı', value: summary.expense?.infrastructure || 0, icon: '🔧' },
        { key: 'permits', label: 'İzin/Ruhsat', value: summary.expense?.permits || 0, icon: '📋' },
        { key: 'utilities', label: 'Faturalar', value: summary.expense?.utilities || 0, icon: '💡' },
        { key: 'other', label: 'Diğer', value: summary.expense?.other || 0, icon: '📦' },
    ].filter(i => i.value > 0).sort((a, b) => b.value - a.value);

    const expenseItems = categoryFilter === 'all'
        ? allExpenseItems
        : allExpenseItems.filter(i => i.key === categoryFilter);

    const filteredTotal = expenseItems.reduce((sum, i) => sum + i.value, 0);

    // En büyük harcamalar (filtrelenmiş: kategoriye göre)
    const categoryLabelMap = { materials: 'Malzeme', labor: 'İşçilik', infrastructure: 'Altyapı', permits: 'İzin', utilities: 'Fatura', other: 'Diğer' };
    const topExpenses = useMemo(() => {
        let txs = (allTransactions || []).filter(t => t.type === 'expense');
        if (categoryFilter !== 'all') {
            const catLabel = categoryLabelMap[categoryFilter];
            if (catLabel) txs = txs.filter(t => t.category === catLabel);
        }
        return txs.sort((a, b) => b.amount - a.amount).slice(0, 5);
    }, [allTransactions, categoryFilter]);

    // Filtrelenmiş borçlar
    const filteredPayables = useMemo(() => {
        if (debtFilter === 'all') return payables;
        return payables.filter(p => p.status === debtFilter);
    }, [payables, debtFilter]);

    return (
        <div className="space-y-6">
            {/* Toplam Gider + Kategori Filtresi */}
            <Card padding="lg">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-error/10 flex items-center justify-center">
                            <TrendingDown size={24} className="text-error" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-text-primary">
                                {categoryFilter === 'all' ? 'Toplam Gider' : allExpenseItems.find(i => i.key === categoryFilter)?.label + ' Giderleri'}
                            </h2>
                            <p className="text-3xl font-bold text-error">{formatCurrency(filteredTotal)}</p>
                        </div>
                    </div>

                    {/* Kategori Filtresi */}
                    <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                        className="appearance-none px-3 py-2 text-sm bg-bg-tertiary border border-border-light rounded-lg text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30">
                        <option value="all">Tüm Kategoriler</option>
                        {allExpenseItems.map(item => (
                            <option key={item.key} value={item.key}>{item.icon} {item.label}</option>
                        ))}
                    </select>
                </div>
                <CategoryGrid items={expenseItems} total={totalExpense} variant="expense" formatCurrency={formatCurrency} />
            </Card>

            {/* Ödeme Durumu */}
            <Card padding="lg">
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                    <Building2 size={18} className="text-text-muted" />
                    Ödeme Durumu
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-success/5 rounded-xl border border-success/20">
                        <p className="text-sm text-text-muted">Ödenen</p>
                        <p className="text-2xl font-bold text-text-primary mt-1">{formatCurrency(summary.payments?.paid || 0)}</p>
                    </div>
                    <div className="p-4 bg-warning/5 rounded-xl border border-warning/20">
                        <p className="text-sm text-text-muted">Bekleyen</p>
                        <p className="text-2xl font-bold text-warning mt-1">{formatCurrency(summary.payments?.pending || 0)}</p>
                    </div>
                    <div className="p-4 bg-info/5 rounded-xl border border-info/20">
                        <p className="text-sm text-text-muted">Planlanan</p>
                        <p className="text-2xl font-bold text-info mt-1">{formatCurrency(summary.payments?.scheduled || 0)}</p>
                    </div>
                </div>
            </Card>

            {/* En Büyük Harcamalar */}
            {topExpenses.length > 0 && (
                <Card padding="lg">
                    <h3 className="text-base font-semibold text-text-primary mb-4 flex items-center gap-2">
                        <TrendingDown size={18} className="text-error" />
                        En Büyük 5 Harcama
                        {categoryFilter !== 'all' && (
                            <span className="text-xs bg-error/10 text-error px-2 py-0.5 rounded-full">
                                {allExpenseItems.find(i => i.key === categoryFilter)?.label}
                            </span>
                        )}
                    </h3>
                    <div className="space-y-2">
                        {topExpenses.map((tx, idx) => (
                            <div key={tx.id} className="flex items-center gap-4 p-3 bg-bg-tertiary rounded-lg">
                                <span className="text-lg font-bold text-text-muted w-6 text-center">{idx + 1}</span>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-text-primary text-sm truncate">{tx.person}</p>
                                    <p className="text-xs text-text-muted truncate">{tx.description}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-base font-bold text-error">{formatCurrency(tx.amount)}</p>
                                    <p className="text-[10px] text-text-muted">{formatDate(tx.date)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {/* Borçlar — Filtrelenebilir */}
            <Card padding="lg">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                        <TrendingDown size={18} className="text-error" />
                        Firmalara Borçlar
                        <span className="text-sm font-normal text-text-tertiary">({filteredPayables.length})</span>
                    </h3>
                    <div className="flex items-center gap-1 bg-bg-tertiary rounded-lg p-1">
                        {[
                            { key: 'all', label: 'Tümü' },
                            { key: 'overdue', label: 'Gecikmiş' },
                            { key: 'pending', label: 'Bekleyen' },
                            { key: 'scheduled', label: 'Planlanan' },
                        ].map(opt => (
                            <button key={opt.key} onClick={() => setDebtFilter(opt.key)}
                                className={clsx("px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors",
                                    debtFilter === opt.key
                                        ? "bg-bg-secondary text-text-primary shadow-sm"
                                        : "text-text-muted hover:text-text-secondary"
                                )}>
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="space-y-2">
                    {filteredPayables.map(item => (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-bg-tertiary rounded-lg hover:bg-bg-hover transition-colors">
                            <div>
                                <p className="text-sm font-medium text-text-primary">{item.name}</p>
                                <p className="text-xs text-text-muted">Sonraki ödeme: {formatDate(item.nextPaymentDate)}</p>
                            </div>
                            <div className="text-right">
                                <p className={clsx("font-semibold", item.status === 'overdue' ? 'text-error' : 'text-text-primary')}>
                                    {formatCurrency(item.totalRemaining)}
                                </p>
                                {item.status === 'overdue' && <span className="text-xs text-error flex items-center gap-1 justify-end"><AlertCircle size={12} /> Gecikmiş</span>}
                                {item.status === 'pending' && <span className="text-xs text-warning flex items-center gap-1 justify-end"><Clock size={12} /> Bekliyor</span>}
                            </div>
                        </div>
                    ))}
                    {filteredPayables.length === 0 && (
                        <p className="text-sm text-text-muted text-center py-4">Seçili filtreye uygun borç yok.</p>
                    )}
                </div>
            </Card>
        </div>
    );
}
